import { useCallback, useRef, useEffect, useState } from "react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import clsx from "clsx";
import type { POI } from "@poi-planner/shared";
import {
  MAX_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  useUIStore,
} from "../store/uiStore";
import { isOwnPoi } from "../store/libraryStore";
import { dndIds } from "../utils/dndIds";
import type { TripSidebarController } from "../hooks/useTripSidebarController";
import { Button } from "./Button";
import { PaneTitleButton } from "./PaneToggle";
import { PoiFilterBar } from "./PoiFilterBar";
import { SidebarPoiCard } from "./SidebarPoiCard";
import { SidebarResizeHandle } from "./SidebarResizeHandle";

const MIN_MAIN_CONTENT_WIDTH = 320;

function getAvailableSidebarWidth(): number {
  return Math.max(0, window.innerWidth - MIN_MAIN_CONTENT_WIDTH);
}

interface SidebarEmptyState {
  icon: string;
  iconColorClass: string;
  title: string;
  subtitle: string;
}

/** One reason drives the icon, glyph, title, and subtitle together, so they
 * can't drift out of agreement the way four independently-maintained
 * ternary ladders can. */
function getSidebarEmptyState(
  totalPoiCount: number,
  hideScheduled: boolean,
): SidebarEmptyState {
  if (totalPoiCount === 0) {
    return {
      icon: "explore",
      iconColorClass: "text-gray-300",
      title: "No POIs yet",
      subtitle: "Create a point of interest to start planning your trip",
    };
  }
  if (hideScheduled) {
    return {
      icon: "check_circle",
      iconColorClass: "text-success",
      title: "All POIs scheduled!",
      subtitle: "Toggle 'Hide planned' off to see all POIs",
    };
  }
  return {
    icon: "search_off",
    iconColorClass: "text-gray-300",
    title: "No matches",
    subtitle: "Try adjusting your search or tag filters",
  };
}

interface SidebarProps {
  controller: TripSidebarController;
  /** Fired when a card is clicked: highlights + flies to it on whichever map
   * pane is visible. Independent of proximity sorting (see onSetTarget). */
  onSelectPoi?: (poiId: string | null) => void;
  /** Whether bounds filter is enabled */
  boundsFilterEnabled?: boolean;
  /** Callback when bounds filter changes */
  onBoundsFilterChange?: (enabled: boolean) => void;
  /** Omitted while the sidebar is the only visible pane. */
  onCollapse?: () => void;
  /** Take the remaining width instead of the user's resizable width, for
   * when the Planner and Map are both collapsed. */
  fill?: boolean;
}

export function Sidebar({
  controller,
  onSelectPoi,
  boundsFilterEnabled,
  onBoundsFilterChange,
  onCollapse,
  fill = false,
}: SidebarProps) {
  const selectedMapPoiId = useUIStore((s) => s.selectedMapPoiId);
  const poiListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedMapPoiId || !poiListRef.current) return;
    const cardEl = poiListRef.current.querySelector(
      `[data-poi-id="${selectedMapPoiId}"]`,
    );
    cardEl?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedMapPoiId]);

  const {
    hideScheduled,
    setHideScheduled,
    targetPoiId,
    setTargetPoiId,
    targetPoi,
    search,
    sortMode,
    sortAscending,
    tagMatchMode,
    tagFilters,
    sourceFilters,
    collectionFilters,
    legFilters,
    ownerFilters,
    maximumDistanceKm,
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    ownerOptions,
    activeFilterCounts,
    collectionOptions,
    legOptions,
    toggleLeg,
    scheduledCounts,
    pinnedPoiIds,
    hasLegs,
    onTogglePin,
    setSearch,
    setSortMode,
    setSortAscending,
    setTagMatchMode,
    setMaximumDistanceKm,
    toggleTag,
    toggleSource,
    toggleCollection,
    toggleOwner,
    clearAllFilters,
    totalPoiCount,
  } = controller;

  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const emptyState = getSidebarEmptyState(totalPoiCount, hideScheduled);

  return (
    <SidebarDropTarget fill={fill}>
      <div className="flex h-13 shrink-0 items-center justify-between gap-3 border-b border-border px-6">
        <PaneTitleButton
          pane="sidebar"
          onCollapse={onCollapse}
          subtitle={
            hideScheduled ? `${filteredPOIs.length} unscheduled` : undefined
          }
        />
        <Button
          onClick={() => openPOIEditor("create")}
          variant="primary"
          icon="add"
        >
          Create
        </Button>
      </div>

      <div className="border-b border-border px-6 pb-6 pt-4">
        <PoiFilterBar
          search={search}
          sortMode={sortMode}
          sortAscending={sortAscending}
          tagFilters={tagFilters}
          allTags={allTags}
          onSearchChange={setSearch}
          onSortModeChange={setSortMode}
          onSortAscendingChange={setSortAscending}
          onToggleTag={toggleTag}
          tagMatchMode={tagMatchMode}
          onTagMatchModeChange={setTagMatchMode}
          sourceFilters={sourceFilters}
          allSourceTexts={allSourceTexts}
          onToggleSource={toggleSource}
          collectionFilters={collectionFilters}
          collections={collectionOptions}
          onToggleCollection={toggleCollection}
          legFilters={legFilters}
          legs={legOptions}
          onToggleLeg={toggleLeg}
          ownerFilters={ownerFilters}
          owners={ownerOptions}
          onToggleOwner={toggleOwner}
          hideScheduled={hideScheduled}
          onHideScheduledChange={setHideScheduled}
          targetPoi={targetPoi}
          onClearTarget={() => setTargetPoiId(null)}
          maximumDistanceKm={maximumDistanceKm}
          onMaximumDistanceChange={setMaximumDistanceKm}
          boundsFilterEnabled={boundsFilterEnabled}
          onBoundsFilterChange={onBoundsFilterChange}
          onClearAllFilters={clearAllFilters}
          activeFilterCounts={activeFilterCounts}
          filteredCount={filteredPOIs.length}
          totalCount={totalPoiCount}
        />
      </div>

      <div
        ref={poiListRef}
        className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 custom-scrollbar"
      >
        {filteredPOIs.length === 0 ? (
          <div className="empty-state py-12 px-6">
            <span
              className={clsx(
                "material-symbols-outlined text-5xl",
                emptyState.iconColorClass,
              )}
            >
              {emptyState.icon}
            </span>
            <div>
              <p className="text-sm font-semibold text-secondary mb-1">
                {emptyState.title}
              </p>
              <p className="text-xs text-tertiary leading-relaxed">
                {emptyState.subtitle}
              </p>
            </div>
            {totalPoiCount === 0 && (
              <Button
                onClick={() => openPOIEditor("create")}
                variant="primary"
                icon="add"
              >
                Create POI
              </Button>
            )}
          </div>
        ) : (
          filteredPOIs.map((poi) => (
            <DraggableSidebarPOICard
              key={poi.id}
              poi={poi}
              scheduledCount={scheduledCounts.get(poi.id) ?? 0}
              distance={distanceMap.get(poi.id)}
              showDistance={!!targetPoiId}
              isTargeted={poi.id === targetPoiId}
              isSelected={poi.id === selectedMapPoiId}
              onSetTarget={setTargetPoiId}
              onSelectPoi={onSelectPoi}
              isPinned={pinnedPoiIds.has(poi.id)}
              isScheduled={(scheduledCounts.get(poi.id) ?? 0) > 0}
              hasLegs={hasLegs}
              onTogglePin={onTogglePin}
            />
          ))
        )}
      </div>
    </SidebarDropTarget>
  );
}

function SidebarDropTarget({
  children,
  fill,
}: {
  children: React.ReactNode;
  fill: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dndIds.sidebar });
  const sidebarWidth = useUIStore((state) => state.sidebarWidth);
  const [availableWidth, setAvailableWidth] = useState(
    getAvailableSidebarWidth,
  );

  useEffect(() => {
    const handleResize = () => setAvailableWidth(getAvailableSidebarWidth());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxWidth = Math.max(
    MIN_SIDEBAR_WIDTH,
    Math.min(MAX_SIDEBAR_WIDTH, availableWidth),
  );
  const visibleWidth = Math.min(sidebarWidth, maxWidth);

  return (
    <aside
      ref={setNodeRef}
      className={clsx(
        "sidebar transition-colors",
        fill && "min-w-0 flex-1",
        isOver && "bg-success-bg",
      )}
      style={fill ? undefined : { width: visibleWidth }}
    >
      {children}
      {!fill && (
        <SidebarResizeHandle width={visibleWidth} maxWidth={maxWidth} />
      )}
    </aside>
  );
}

interface DraggableSidebarPOICardProps {
  poi: POI;
  scheduledCount: number;
  distance?: number;
  showDistance: boolean;
  isTargeted?: boolean;
  onSetTarget?: (poiId: string | null) => void;
  isSelected?: boolean;
  onSelectPoi?: (poiId: string | null) => void;
  isPinned?: boolean;
  isScheduled?: boolean;
  hasLegs?: boolean;
  onTogglePin?: (poiId: string) => Promise<string | null>;
}

function DraggableSidebarPOICard({
  poi,
  scheduledCount,
  distance,
  showDistance,
  isTargeted,
  onSetTarget,
  isSelected,
  onSelectPoi,
  isPinned,
  isScheduled,
  hasLegs,
  onTogglePin,
}: DraggableSidebarPOICardProps) {
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const wasDragging = useRef(false);
  const [pinMessage, setPinMessage] = useState<string | null>(null);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.sidebarItem(poi.id),
    data: { type: "sidebar-poi", poiId: poi.id },
  });

  useEffect(() => {
    if (isDragging) wasDragging.current = true;
  }, [isDragging]);

  useEffect(() => {
    if (!pinMessage) return;
    const t = setTimeout(() => setPinMessage(null), 2500);
    return () => clearTimeout(t);
  }, [pinMessage]);

  const handleClick = useCallback(() => {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
    onSelectPoi?.(poi.id);
  }, [onSelectPoi, poi.id]);

  const handleEdit = useCallback(
    () => openPOIEditor("edit", poi.id),
    [openPOIEditor, poi.id],
  );
  const canEdit = isOwnPoi(poi);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={clsx("relative", isDragging && "opacity-40")}
    >
      <SidebarPoiCard
        poi={poi}
        scheduledCount={scheduledCount}
        distance={distance}
        showDistance={showDistance}
        isTargeted={isTargeted}
        onSetTarget={onSetTarget}
        isSelected={isSelected}
        isDraggable
        onClick={handleClick}
        onEdit={handleEdit}
        canEdit={canEdit}
        isPinned={isPinned}
        isScheduled={isScheduled}
        hasLegs={hasLegs}
        onTogglePin={onTogglePin}
        onShowPinMessage={setPinMessage}
      />
      {pinMessage && (
        <div className="absolute -bottom-1 left-0 right-0 translate-y-full z-10 pointer-events-none">
          <div className="mx-2 mt-1 px-2.5 py-1.5 bg-slate-800 text-white text-xs rounded-lg shadow-lg text-center">
            {pinMessage}
          </div>
        </div>
      )}
    </div>
  );
}
